/**
 * @file OrdersListPage.tsx
 * @description Реестр заказов (phase10; phase19.24 §14 — «Мой ход»;
 * phase19.31 — макет (а) разбора A: таблица от 1024, компактный список на
 * телефоне, постраничный вывод).
 *
 * Страница — раскладка: заголовок в верхней панели (`CabinetPageHeader`),
 * фильтры, список, страницы. Данные и состояние в адресе держит
 * `useOrdersRegistry`, колонки — `ORDER_REGISTRY_COLUMNS`.
 *
 * Плиток показателей нет (SC47): они считались по первым ста заказам и
 * показывали неверные числа. «Требуют внимания N» — обычная кнопка-контур со
 * счётчиком, а не красная заливка: это переход, а не тревога. «Доска заказов»
 * и «Обновить» — тоже видимыми кнопками, меню «⋯» у шапки нет (SC67).
 */

import { LayoutGrid, Plus, RefreshCw, TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { Link, useParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { OrdersFilterBar } from "../../../components/orders/OrdersFilterBar";
import { ORDER_REGISTRY_COLUMNS } from "../../../components/orders/registry/order-columns";
import { ordersEmptySpec, ordersFilteredSpec } from "../../../components/orders/registry/orders-empty";
import { OrdersPagination } from "../../../components/orders/registry/OrdersPagination";
import { Button } from "../../../components/ui/button";
import { PageActionButton } from "../../../components/ui/cabinet-page-header-parts";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import type { PageAction } from "../../../components/ui/page-actions";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import { useOrdersRegistry } from "../../../hooks/useOrdersRegistry";
import { useOrderStageOptions } from "../../../hooks/useOrderStageOptions";
import { useShipmentAttentionCount } from "../../../hooks/useShipmentAttentionCount";
import { ordersFilterKind } from "../../../lib/order-registry-query";
import { pluralRu } from "../../../lib/plural";

export function OrdersListPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = `/shop/${slug}/orders`;
  const canManage = useHasPermission("SHOP_MANAGE_ORDERS");
  // Зависшие отправления (phase19.13 шаг 5) — вход в очередь прямо из реестра.
  const attentionCount = useShipmentAttentionCount();
  const registry = useOrdersRegistry();
  const stageOptions = useOrderStageOptions();
  const { query, setQuery } = registry;
  const filterKind = ordersFilterKind(query);

  // Все действия шапки — видимыми кнопками, без «⋯» (SC67): их три, на
  // телефоне и планшете они встают строкой под шапкой.
  const actions = useMemo<PageAction[]>(
    () => [
      {
        key: "attention",
        label: attentionCount > 0 ? `Требуют внимания ${attentionCount}` : "Требуют внимания",
        icon: TriangleAlert,
        to: `${base}/attention`,
      },
      { key: "board", label: "Доска заказов", icon: LayoutGrid, to: `${base}/board` },
      { key: "refresh", label: "Обновить", icon: RefreshCw, run: registry.refresh, loading: registry.loading },
    ],
    [attentionCount, base, registry.refresh, registry.loading],
  );

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Заказы"
        // Пока список не пришёл (или не пришёл из-за ошибки), числа нет: «0 заказов» читалось бы как пусто.
        subtitle={
          registry.items.length === 0 && (registry.loading || registry.error)
            ? undefined
            : `${registry.total} ${pluralRu(registry.total, ["заказ", "заказа", "заказов"])} · новые сверху`
        }
        secondaryActions={actions.map((action) => (
          <PageActionButton key={action.key} action={action} />
        ))}
        action={
          canManage ? (
            <Button variant="brand" asChild>
              <Link to={`${base}/new`}>
                <Plus className="size-4" />
                Новый заказ
              </Link>
            </Button>
          ) : undefined
        }
      />

      <OrdersFilterBar query={query} onChange={setQuery} stageOptions={stageOptions} />

      <ResponsiveList
        aria-label="Реестр заказов"
        rows={registry.items}
        columns={ORDER_REGISTRY_COLUMNS}
        rowKey={(order) => order.id}
        rowHref={(order) => `${base}/${order.id}`}
        rowLabel={(order) => `Заказ ${order.number}`}
        rowSize="double"
        tableMinWidth="min-w-[940px]"
        loading={registry.loading}
        error={registry.error}
        onRetry={registry.refresh}
        filtered={filterKind !== "none"}
        skeletonRows={10}
        empty={ordersEmptySpec(canManage ? `${base}/new` : null)}
        filteredEmpty={ordersFilteredSpec({
          kind: filterKind,
          query,
          onShowAll: () => setQuery({ owner: "all" }),
          onReset: registry.resetFilters,
        })}
      />

      <OrdersPagination
        page={query.page}
        pageSize={query.pageSize}
        total={registry.total}
        onPageChange={(page) => setQuery({ page })}
        onPageSizeChange={(pageSize) => setQuery({ pageSize })}
      />
    </div>
  );
}
